import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { useSession } from '@/lib/useSession';
import {
  canUseNativeKioskEnrollment,
  enrollNativeKioskFromManagedConfiguration,
} from '@/lib/nativeKioskEnrollment';

type Props = {
  children: ReactNode;
};

// In local dev (`npm run dev`), the real API isn't running, so the session
// fetch fails and the redirect to /api/v1/core/auth/login dead-ends. Skip the
// gate in dev so the shell can be previewed without standing up the API. Set
// VITE_AUTH_BYPASS=false in `.env.local` to test the real flow locally.
const DEV_BYPASS = import.meta.env.DEV && import.meta.env.VITE_AUTH_BYPASS !== 'false';

/**
 * Wrap protected content. If the session check returns "not authenticated"
 * the browser is sent to the in-app /signin page, with the current path
 * passed as `return`. The sign-in page asks for the user's email and routes
 * to the right identity provider (Entra for @ampersandbrands.com, Google for
 * @lolliandpops.com), then the API kicks off the OIDC flow.
 *
 * While the session is loading, render a tiny placeholder that matches the
 * page chrome so the redirect feels instant.
 *
 * Copied from the cockpit UI's shell/AuthGate.tsx (same auth flow, same
 * shared API platform).
 */
export function AuthGate({ children }: Props) {
  const { user, isLoading, isError, isForbidden } = useSession();

  useEffect(() => {
    if (DEV_BYPASS) return;
    if (!isLoading && !isError && !isForbidden && user == null) {
      let cancelled = false;
      const openSignIn = () => {
        const returnTo = `${window.location.pathname}${window.location.search}`;
        const params = new URLSearchParams({ return: returnTo });
        window.location.assign(`/signin?${params.toString()}`);
      };

      const routeUnauthenticatedUser = async () => {
        if (!canUseNativeKioskEnrollment()) {
          openSignIn();
          return;
        }

        try {
          const result = await enrollNativeKioskFromManagedConfiguration();
          if (cancelled || result.status === 'started') return;
          if (result.mode === 'employee') {
            openSignIn();
            return;
          }
          window.location.assign('/tablet-enroll');
        } catch (managedError) {
          console.error('[sweetspot-managed-enrollment] Could not read managed configuration', managedError);
          if (!cancelled) window.location.assign('/tablet-enroll');
        }
      };

      void routeUnauthenticatedUser();
      return () => {
        cancelled = true;
      };
    }
  }, [user, isLoading, isError, isForbidden]);

  if (DEV_BYPASS) {
    return <>{children}</>;
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-candy-cream text-sm text-candy-ink-muted dark:bg-candy-dark-bg dark:text-candy-dark-ink-muted">
        Checking your session...
      </div>
    );
  }

  if (isForbidden) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-candy-cream px-4 text-center text-candy-ink-muted dark:bg-candy-dark-bg dark:text-candy-dark-ink-muted">
        <p className="text-base font-medium text-candy-ink dark:text-candy-dark-ink">Access denied</p>
        <p className="text-sm">
          You&apos;re signed in but don&apos;t have access to The Sweet Spot. Contact IT to
          request access.
        </p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-candy-cream text-sm text-red-700 dark:bg-candy-dark-bg dark:text-red-300">
        Could not reach the API. Reload to retry.
      </div>
    );
  }

  if (user == null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-candy-cream text-sm text-candy-ink-muted dark:bg-candy-dark-bg dark:text-candy-dark-ink-muted">
        Opening The Sweet Spot...
      </div>
    );
  }

  return <>{children}</>;
}
